import { type ReactNode } from 'react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { capture } from '@/lib/posthog';
import type { AddInstrumentAnalyticsSource, AnalyticsEventMap } from '@/lib/analytics-events';

/**
 * The entry points that offer the choice. The other two surfaces in the event's
 * union (`table_placeholder`, `context_menu`) deliberately go straight to the
 * Satepsi catalog and never mount this menu.
 */
type AddInstrumentSurface = Extract<
    AnalyticsEventMap['report_instrument_add_clicked']['surface'],
    'panel' | 'empty_state'
>;

interface InstrumentMenuItem {
    toolId: string;
    source: AddInstrumentAnalyticsSource;
    title: string;
    description: string;
}

/**
 * The three ways to add an instrument, in one place. The copy is the reason
 * this component exists: two entry points describing the same choice in two
 * different ways is how the panel and the canvas drift apart.
 *
 * The clipboard route lands a `free_mode` table like the middle one does — it
 * differs only in where the columns and the scores come from, which is exactly
 * what the third description has to say.
 */
const INSTRUMENT_MENU_ITEMS: InstrumentMenuItem[] = [
    {
        toolId: 'tool_test',
        source: 'satepsi',
        title: 'Instrumento do Satepsi',
        description:
            'Busque no catálogo. A tabela vem com as colunas do teste e a referência sai na nota de rodapé.',
    },
    {
        toolId: 'tool_test_free',
        source: 'free_mode',
        title: 'Instrumento Livre',
        description:
            'Para o que não está no catálogo. Você escreve o nome, define as colunas e a referência.',
    },
    {
        toolId: 'tool_test_paste',
        source: 'clipboard',
        title: 'Colar uma tabela pronta',
        description:
            'Do Excel, do Google Sheets, do Docs ou do Word. As colunas e os escores vêm do que você copiou.',
    },
];

interface AddInstrumentMenuProps {
    /** Which entry point fired it — the only thing the event needs from the caller. */
    surface: AddInstrumentSurface;
    /** Runs with the chosen tool id; the click is already recorded by then. */
    onSelect: (toolId: string) => void;
    disabled?: boolean;
    /** `start` under a full-width card, `center` under a centered button. */
    align?: 'start' | 'center' | 'end';
    /** The trigger. Must forward its ref and spread props (Radix `asChild`). */
    children: ReactNode;
}

/**
 * "Adicionar instrumento", offered as the two things it can actually mean.
 *
 * Owns the copy and the analytics; the caller only says where it lives and what
 * to do with the tool id that comes back. `instrument_source` is derived from
 * the item rather than handed back, because the event is the only thing that
 * ever needed it.
 */
export default function AddInstrumentMenu({
    surface,
    onSelect,
    disabled = false,
    align = 'start',
    children,
}: AddInstrumentMenuProps) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild disabled={disabled}>
                {children}
            </DropdownMenuTrigger>
            <DropdownMenuContent align={align} className="w-72">
                {INSTRUMENT_MENU_ITEMS.map((item) => (
                    <DropdownMenuItem
                        key={item.toolId}
                        className="h-auto cursor-pointer items-start gap-3 py-2.5"
                        onClick={() => {
                            capture('report_instrument_add_clicked', {
                                surface,
                                instrument_source: item.source,
                            });
                            onSelect(item.toolId);
                        }}
                    >
                        <div className="space-y-1">
                            <p className="text-sm font-medium leading-tight">{item.title}</p>
                            <p className="text-xs leading-snug text-muted-foreground">
                                {item.description}
                            </p>
                        </div>
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
